import type { AuthTokens, User } from "@/types";
import {
  authService,
  type AuthResponse,
  type SignInChallenge,
} from "@services/api/auth.service";
import { CanceledError } from "axios";
import { create } from "zustand";
import {
  clearOfflineSession,
  isNetworkError,
  isSessionRefused,
  offlineIdentity,
  readOfflineSession,
  saveOfflineSession,
} from "../offline/session";
import { tellOfflineWorker } from "../offline/worker";
import { useOutbox } from "./outbox.store";

interface SessionOperation {
  epoch: number;
  promise: Promise<void>;
}
let refreshInFlight: SessionOperation | null = null;
let checkInFlight: SessionOperation | null = null;

const LAST_TENANT_STORAGE_KEY = "fh:last-tenant-id";
const SUPER_ADMIN_SYNTHETIC_TENANT_ID = "00000000-0000-0000-0000-000000000000";

/** Keeps what the app needs to open offline for this person, and points saved data at them. */
function rememberForOffline(user: User) {
  saveOfflineSession(user);
  const identity = offlineIdentity(user);
  tellOfflineWorker({ type: "identity", identity });
  useOutbox.getState().load(identity);
}

/** The server refused the session: nothing saved for it may be shown offline any more. */
function forgetOfflineData() {
  clearOfflineSession();
  tellOfflineWorker({ type: "clear" });
}

function persistLastTenantId(
  tenantId?: string | null,
  role?: User["role"] | null,
) {
  if (typeof window === "undefined") return;
  try {
    if (
      tenantId &&
      !(role === "SUPER_ADMIN" && tenantId === SUPER_ADMIN_SYNTHETIC_TENANT_ID)
    ) {
      window.localStorage.setItem(LAST_TENANT_STORAGE_KEY, tenantId);
    } else {
      window.localStorage.removeItem(LAST_TENANT_STORAGE_KEY);
    }
  } catch {
    // Storage availability must not prevent clearing an authenticated session.
  }
}

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /** In-memory generation. Explicit login/logout invalidates older asynchronous operations. */
  sessionEpoch: number;
  /** Opened from what this device saved, without reaching the server; renews once back online. */
  offline: boolean;
  /**
   * The second step a password sign-in is waiting on. Kept here, not in the page: a new
   * session generation remounts the page tree.
   */
  challenge: SignInChallenge | null;
  /** Resolves with a challenge when a second step (two-factor) is needed before the session starts. */
  login: (email: string, password: string) => Promise<SignInChallenge | null>;
  /** Starts the session a finished sign-in returned (password, two-factor, Google or Microsoft). */
  completeSignIn: (response: AuthResponse) => void;
  /** Back to the password form from the two-factor step. */
  clearChallenge: () => void;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  setLoading: (loading: boolean) => void;
  clearError: () => void;
  checkAuth: () => Promise<void>;
}

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === "object") {
    const maybeResponse = error as {
      response?: { data?: { message?: string } };
    };
    return maybeResponse.response?.data?.message || fallback;
  }
  return fallback;
};

const sameActor = (a: User, b: User) =>
  a.id === b.id && a.tenantId === b.tenantId;
const sessionChanged = () =>
  new CanceledError("Authentication session changed");

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  sessionEpoch: 0,
  offline: false,
  challenge: null,

  login: async (email, password) => {
    const epoch = get().sessionEpoch + 1;
    set({
      sessionEpoch: epoch,
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      challenge: null,
    });
    try {
      const response = await authService.login({ email, password });
      if (get().sessionEpoch !== epoch) throw sessionChanged();
      if ("mfaRequired" in response) {
        set({ isLoading: false, challenge: response });
        return response;
      }
      get().completeSignIn(response);
      return null;
    } catch (error) {
      if (get().sessionEpoch !== epoch) throw sessionChanged();
      set({
        error: getApiErrorMessage(error, "Login failed"),
        isLoading: false,
      });
      throw error;
    }
  },

  completeSignIn: (response) => {
    persistLastTenantId(response.user.tenantId, response.user.role);
    rememberForOffline(response.user);
    // A session check started before this sign-in must not undo it.
    set({
      sessionEpoch: get().sessionEpoch + 1,
      user: response.user,
      tokens: {
        accessToken: response.accessToken,
        expiresIn: response.expiresIn,
      },
      isAuthenticated: true,
      isLoading: false,
      error: null,
      offline: false,
      challenge: null,
    });
  },

  clearChallenge: () => set({ challenge: null }),

  logout: async () => {
    // The synchronous transport interceptor captures the outgoing session's token first.
    let request: Promise<void> | undefined;
    try {
      request = authService.logout();
    } catch {
      // A synchronous transport failure must also leave the browser signed out.
    }
    persistLastTenantId(null);
    // Data saved for offline use goes with the session. Unsent follow-ups stay until the
    // person who wrote them signs in again, unless they chose to discard them.
    forgetOfflineData();
    set({
      sessionEpoch: get().sessionEpoch + 1,
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      offline: false,
      challenge: null,
    });
    try {
      await request;
    } catch {
      // Local logout is immediate even when the server is unreachable or a newer login wins.
    }
  },

  refreshToken: () => {
    const epoch = get().sessionEpoch;
    if (refreshInFlight?.epoch === epoch) return refreshInFlight.promise;
    const actor = get().user;
    const operation: SessionOperation = { epoch, promise: Promise.resolve() };
    operation.promise = (async () => {
      try {
        const response = await authService.refreshToken();
        if (get().sessionEpoch !== epoch) throw sessionChanged();
        if (actor && !sameActor(actor, response.user))
          throw new Error("Refreshed session identity does not match");
        persistLastTenantId(response.user.tenantId, response.user.role);
        rememberForOffline(response.user);
        set({
          user: response.user,
          tokens: {
            accessToken: response.accessToken,
            expiresIn: response.expiresIn,
          },
          isAuthenticated: true,
          offline: false,
        });
      } catch (error) {
        if (get().sessionEpoch !== epoch) throw sessionChanged();
        // No answer is not a refusal: whoever is signed in stays signed in.
        if (isNetworkError(error)) throw error;
        if (isSessionRefused(error)) forgetOfflineData();
        persistLastTenantId(null);
        set({ user: null, tokens: null, isAuthenticated: false, offline: false });
        throw error;
      } finally {
        if (refreshInFlight === operation) refreshInFlight = null;
      }
    })();
    refreshInFlight = operation;
    return operation.promise;
  },

  updateUser: (userData) => {
    const { user, sessionEpoch } = get();
    if (!user) return;
    const updated = { ...user, ...userData };
    if (!sameActor(user, updated)) {
      persistLastTenantId(null);
      set({
        user: null,
        tokens: null,
        isAuthenticated: false,
        isLoading: false,
        sessionEpoch: sessionEpoch + 1,
      });
      return;
    }
    persistLastTenantId(updated.tenantId, updated.role);
    saveOfflineSession(updated);
    set({ user: updated });
  },
  setLoading: (loading) => set({ isLoading: loading }),
  clearError: () => set({ error: null }),

  checkAuth: () => {
    const epoch = get().sessionEpoch;
    if (checkInFlight?.epoch === epoch) return checkInFlight.promise;
    const operation: SessionOperation = { epoch, promise: Promise.resolve() };
    set({ isLoading: true });
    operation.promise = (async () => {
      try {
        if (!get().tokens?.accessToken) {
          await get().refreshToken();
          if (get().sessionEpoch !== epoch) return;
          if (get().user) {
            set({ isAuthenticated: true, isLoading: false });
            return;
          }
        }
        const actor = get().user;
        const user = await authService.getCurrentUser();
        if (get().sessionEpoch !== epoch) return;
        if (actor && !sameActor(actor, user))
          throw new Error("Current session identity does not match");
        persistLastTenantId(user.tenantId, user.role);
        rememberForOffline(user);
        set({ user, isAuthenticated: true, isLoading: false, offline: false });
      } catch (error) {
        if (get().sessionEpoch !== epoch) return;
        // The server cannot be reached: open with what this device saved for this person.
        // The saved session is not renewed here; only the server can extend it.
        const saved = isNetworkError(error) ? readOfflineSession() : null;
        if (saved) {
          const identity = offlineIdentity(saved);
          tellOfflineWorker({ type: "identity", identity });
          useOutbox.getState().load(identity);
          set({ user: saved, tokens: null, isAuthenticated: true, isLoading: false, offline: true });
          return;
        }
        if (isSessionRefused(error)) forgetOfflineData();
        persistLastTenantId(null);
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
        });
      } finally {
        if (checkInFlight === operation) checkInFlight = null;
      }
    })();
    checkInFlight = operation;
    return operation.promise;
  },
}));

// Back online after opening offline: renew the session quietly, without reloading the page.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    if (useAuthStore.getState().offline) void useAuthStore.getState().refreshToken().catch(() => undefined);
  });
}
